import { useCallback, useEffect, useMemo } from 'react';
import Grid from '@mui/material/Grid';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useWatch } from 'react-hook-form';
import { OptionsObject, enqueueSnackbar } from 'notistack';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSelector } from 'react-redux';
import { AppPermissions, CreateReprocessForm } from '../../types';
import {
  ResetWhenFieldChanges,
  CountrySelectFormInput,
  DatePickerFormInput,
  FlexSalesForm,
  StoreSelectFormInput,
  TextFieldFormInput,
  useFlexSalesForm,
  FlexSalesFormActions,
} from '../FlexSalesForm';

import routes from '../../config/routes';
import { PermissionsProvider } from '../Permissions';
import { useListener } from '../../hooks';
import { ReprocessActionType, reprocessMaxDateRangeSelector } from '../../state';

import { getSchema, COUNTRIES_OPTIONAL_STORE } from './schema';

const initialValues: CreateReprocessForm = {
  country: '',
  store: '',
  justification: '',
  businessDateFrom: null,
  businessDateTo: null,
};

const resourcePermissions = [AppPermissions.REPROCESS];

const messageOptions: OptionsObject = {
  variant: 'success',
  autoHideDuration: 6000,
  anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
};

const StoreFormInput = () => {
  const { control } = useFlexSalesForm();
  const country = useWatch({ control, name: 'country' });
  const disabled = ['EC', 'UY'].includes(country);

  return (
    <StoreSelectFormInput
      name="store"
      countryCode={country || ''}
      required={!COUNTRIES_OPTIONAL_STORE.includes(country)}
      disabled={disabled}
      fullWidth
    />
  );
};

const ConditionalCountrySelectFormInput = () => {
  const { control } = useFlexSalesForm();
  const allFailed = useWatch({ control, name: 'allFailed' });
  return <CountrySelectFormInput name="country" fullWidth required disabled={allFailed} />;
};

const InnerForm = ({ onCancel }: { onCancel: () => void }) => {
  const { t } = useTranslation();

  const { control, trigger } = useFlexSalesForm();
  const businessDateFrom = useWatch({ control, name: 'businessDateFrom' });

  useEffect(() => {
    trigger(['businessDateFrom', 'businessDateTo']);
  }, [businessDateFrom]);

  return (
    <>
      <ResetWhenFieldChanges field="country" fieldToReset="store" />
      <ResetWhenFieldChanges field="country" fieldToReset="stores" />
      <ResetWhenFieldChanges field="country" fieldToReset="groupIds" />
      <Grid container flexDirection="column" rowSpacing={1}>
        <Grid item xs>
          <ConditionalCountrySelectFormInput />
        </Grid>
        <Grid item xs>
          <StoreFormInput />
        </Grid>
        <Grid item xs>
          <DatePickerFormInput
            name="businessDateFrom"
            label={t('reprocess.labels.since')}
            required
          />
        </Grid>
        <Grid item xs>
          <DatePickerFormInput name="businessDateTo" label={t('reprocess.labels.until')} required />
        </Grid>
        <Grid item xs>
          <TextFieldFormInput
            name="justification"
            variant="standard"
            label={t('reprocess.labels.justification')}
            required
            fullWidth
          />
        </Grid>
      </Grid>
      <FlexSalesFormActions
        onCancel={onCancel}
        cancelLabel={t('reprocess.labels.cancel')}
        submitLabel={t('reprocess.labels.create')}
      />
    </>
  );
};

const ReprocessForm = () => {
  const { t } = useTranslation();

  const maxDaysConfig = useSelector(reprocessMaxDateRangeSelector);

  const resolver = useMemo(() => {
    const limit = maxDaysConfig || 5;
    return yupResolver(getSchema(limit));
  }, [maxDaysConfig]);

  const formListener = useListener(
    ReprocessActionType.reprocessCreateRequested,
    ReprocessActionType.reprocessCreateSuccess,
    ReprocessActionType.reprocessCreateFailure
  );

  const navigate = useNavigate();
  const handleCancel = useCallback(() => {
    navigate(routes.protectedNestedRoutes.reprocess.index.path);
  }, [navigate]);

  const onSubmit = async (data: CreateReprocessForm) => {
    await formListener(data);
    handleCancel();
    enqueueSnackbar(t('reprocess.messages.success'), messageOptions);
  };
  return (
    <PermissionsProvider resourcePermissions={resourcePermissions}>
      <FlexSalesForm onValidSubmit={onSubmit} resolver={resolver} defaultValues={initialValues}>
        <InnerForm onCancel={handleCancel} />
      </FlexSalesForm>
    </PermissionsProvider>
  );
};

export default ReprocessForm;
